import React from 'react';
import { Button, Checkbox, Empty, Icon, Select, Spin } from 'antd';
import cn from 'classnames';
import { useEffect, useState } from 'react';
import Card, { CardHeader } from '../../../../../../components/Card';
import SearchField from '../../../../../../components/SearchField/SearchField';
import { getReplacedString } from '../../../../../../helpers/getReplacedString';
import useDebouncedSearch from '../../../../../../hooks/useDebouncedSearch';
import styles from './BindEntityCard.module.scss';

const emptyFilters = [];

const filterItems = ({ items, search, getSearchContent, filters }) =>
  items.filter((item) => {
    const isSearchMatched = (() => {
      if (!search) return true;

      const searchContent = getSearchContent(item);
      const value = getReplacedString(search);
      const replacedSearchContent = getReplacedString(searchContent);

      return replacedSearchContent.includes(value);
    })();

    const areFiltersMatched = filters.every((filter) => {
      if (filter.value === undefined || filter.value === null) return true;

      return String(filter.getItemValue(item)) === String(filter.value);
    });

    return isSearchMatched && areFiltersMatched;
  });

const searchDelay = 600;

const Content = ({ text, onClick, selected }) => {
  const hasOnClick = typeof onClick === 'function';

  const Container = hasOnClick ? 'button' : 'div';

  return (
    <Container
      onClick={hasOnClick ? onClick : undefined}
      className={cn(styles.contentWrap, {
        [styles.action]: hasOnClick,
        [styles.selectedAction]: selected,
      })}
    >
      <span>{text}</span>
      {hasOnClick && <Icon type="caret-right" />}
    </Container>
  );
};

const ListItem = ({
  item,
  selected,
  onChange,
  onSelectBtnClick,
  checked,
  indeterminate,
  text,
}) => {
  const handleChange = (e) => {
    onChange(item, e.target.checked);
  };

  return (
    <li
      className={cn(styles.listItem, {
        [styles.selected]: selected,
      })}
    >
      <Checkbox
        checked={checked}
        indeterminate={indeterminate}
        onChange={handleChange}
      />

      <Content
        text={text}
        onClick={
          typeof onSelectBtnClick === 'function'
            ? () => onSelectBtnClick(item.id)
            : undefined
        }
        selected={selected}
      />
    </li>
  );
};

const BindEntityCard = ({
  items,
  isSelected,
  loading,
  onSelectBtnClick,
  onItemChange,
  isChecked,
  isIndeterminate,
  renderContent,
  className,
  title,
  onClose,
  getSearchContent,
  onSelectAll,
  onUnselectAll,
  filters = emptyFilters,
}) => {
  const [currentItems, setCerrentItems] = useState(items || []);
  const [filterValues, setFilterValues] = useState({});
  const [search, setSearch, debouncedSearch] = useDebouncedSearch(searchDelay);

  useEffect(() => {
    setFilterValues((prev) => {
      let next = prev;

      filters.forEach((filter) => {
        const value = prev[filter.name];
        const hasValue = value !== undefined && value !== null;
        const hasOption = filter.options.some(
          (option) => String(option.value) === String(value)
        );

        if (hasValue && !hasOption) {
          next = next === prev ? { ...prev } : next;
          delete next[filter.name];
        }
      });

      return next;
    });
  }, [filters]);

  useEffect(() => {
    let newItems = [];

    if (items?.length) {
      newItems = filterItems({
        items,
        search: debouncedSearch?.trim(),
        getSearchContent,
        filters: filters.map((filter) => ({
          ...filter,
          value: filterValues[filter.name],
        })),
      });
    }

    setCerrentItems(newItems);
  }, [items, debouncedSearch, getSearchContent, filters, filterValues]);

  const handleFilterChange = (name, value) => {
    setFilterValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Card
      dark
      className={cn(styles.root, className)}
    >
      {loading && <Spin className={styles.spin} />}
      <CardHeader
        title={title}
        titleProps={{ className: styles.title }}
        action={
          typeof onClose === 'function' ? (
            <Button
              icon="close"
              onClick={onClose}
              type="ghost"
              className={styles.closeBtn}
            />
          ) : undefined
        }
      />
      {typeof onSelectAll === 'function' &&
        typeof onUnselectAll === 'function' && (
          <div className={styles.massSelection_container}>
            <Button
              type="link"
              onClick={() => onSelectAll?.(currentItems)}
            >
              Выбрать все
            </Button>
            <Button
              type="link"
              onClick={() => onUnselectAll?.(currentItems)}
            >
              Снять все
            </Button>
          </div>
        )}
      <SearchField
        className={styles.search}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {filters.length > 0 &&
        filters.map((filter) => (
          <Select
            key={filter.name}
            allowClear
            showSearch
            className={styles.filter}
            placeholder={filter.placeholder}
            optionFilterProp="searchtext"
            value={filterValues[filter.name]}
            onChange={(value) => handleFilterChange(filter.name, value)}
          >
            {filter.options.map((option) => (
              <Select.Option
                key={option.value}
                value={option.value}
                searchtext={option.label}
              >
                {option.label}
              </Select.Option>
            ))}
          </Select>
        ))}
      <ul className={styles.list}>
        {!currentItems.length && (
          <li className={styles.emptyItem}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </li>
        )}

        {currentItems.length > 0 &&
          currentItems.map((item) => {
            return (
              <ListItem
                key={item.id}
                item={item}
                selected={
                  typeof isSelected === 'function' ? isSelected(item.id) : false
                }
                onSelectBtnClick={onSelectBtnClick}
                onChange={onItemChange}
                checked={isChecked(item)}
                indeterminate={
                  typeof isIndeterminate === 'function'
                    ? isIndeterminate(item)
                    : false
                }
                text={renderContent(item)}
              />
            );
          })}
      </ul>
    </Card>
  );
};

export default BindEntityCard;
